/**
 * Phase 3b — Counterparty contact rows.
 *
 * Renders the backend-enriched contacts (role_group + touch_count +
 * employer_domain) as the role-grouped + company-sub-grouped + touch-
 * count-sorted list described in spec §4.3.
 *
 * Display order: target → intermediary → advisors → others. Groups
 * with no rows collapse. Within a group: sub-group by company,
 * sort sub-groups by max(touch_count) desc, sort rows within a sub-
 * group by touch_count desc, name asc.
 *
 * Edit affordance (per-row pencil) opens `ContactEditModal`. The
 * value coming OUT of the editor is the same list shape going IN
 * — the backend will re-enrich on the next aggregate pass.
 */
import { useMemo, useState } from 'react';

import { ContactEditModal, type ContactDraft } from './DealsPage.ContactEditModal';
import styles from './DealsPage.module.css';

export interface Contact {
  name?: string;
  email?: string | null;
  role?: string | null;
  company?: string | null;
  // Phase 3b enrichment fields — populated by the backend when
  // deals_structured_contacts_enabled is on. Optional so the FE
  // gracefully degrades when the flag is off.
  employer_domain?: string | null;
  org_type?: string | null;
  role_group?: 'target' | 'intermediary' | 'advisors' | 'others' | string | null;
  touch_count?: number | null;
}

const GROUP_ORDER: Array<{ key: string; label: string }> = [
  { key: 'target', label: 'Target' },
  { key: 'intermediary', label: 'Intermediary' },
  { key: 'advisors', label: 'Advisors' },
  { key: 'others', label: 'Others' },
];

interface Props {
  value: Contact[];
  onChange?: (next: Contact[]) => void;
  disabled?: boolean;
}

export function ContactRowEditor({ value, onChange, disabled }: Props) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  // Group by role_group → company → contacts.
  const grouped = useMemo(() => groupContacts(value), [value]);
  const editable = !!onChange && !disabled;

  return (
    <div className={styles.contactGroupContainer}>
      {grouped.map(({ key, label, subGroups }) =>
        subGroups.length === 0 ? null : (
          <div key={key} className={styles.contactGroup}>
            <div className={styles.contactGroupHeader}>{label}</div>
            {subGroups.map((sg) => (
              <div key={sg.companyKey} className={styles.contactSubGroup}>
                {sg.companyDisplay && (
                  <div className={styles.contactCompanyLabel}>
                    {sg.companyDisplay}
                  </div>
                )}
                {sg.contacts.map((c) => (
                  <div
                    key={`${c.index}`}
                    className={styles.contactRow}
                  >
                    <span className={styles.contactName}>
                      {c.name || '(no name)'}
                      {c.role_group === undefined && (
                        <span
                          className={styles.contactUnflagged}
                          title="Backend enrichment unavailable — flag may be off."
                        >
                          ⚑
                        </span>
                      )}
                    </span>
                    {c.role && (
                      <span className={styles.contactRole}>{c.role}</span>
                    )}
                    {c.email && (
                      <span className={styles.contactEmail}>{c.email}</span>
                    )}
                    {typeof c.touch_count === 'number' && c.touch_count > 0 && (
                      <span
                        className={styles.contactTouchCount}
                        title={`${c.touch_count} events`}
                      >
                        ·{c.touch_count}
                      </span>
                    )}
                    {editable && (
                      <button
                        type="button"
                        className={styles.contactEditBtn}
                        onClick={() => setEditingIndex(c.index)}
                        title="Edit contact"
                      >
                        edit
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ),
      )}
      {value.length === 0 && (
        <div className={styles.contactGroupEmpty}>(no contacts proposed)</div>
      )}

      {editable && editingIndex !== null && (
        <ContactEditModal
          draft={value[editingIndex] as ContactDraft}
          onCancel={() => setEditingIndex(null)}
          onSave={(updated) => {
            const next = value.slice();
            next[editingIndex] = { ...next[editingIndex], ...updated };
            onChange?.(next);
            setEditingIndex(null);
          }}
        />
      )}
    </div>
  );
}


interface SubGroup {
  companyKey: string;
  companyDisplay: string | null;
  maxTouch: number;
  contacts: Array<Contact & { index: number }>;
}

interface Group {
  key: string;
  label: string;
  subGroups: SubGroup[];
}


function groupContacts(value: Contact[]): Group[] {
  const byGroup: Record<string, Array<Contact & { index: number }>> = {};
  value.forEach((c, i) => {
    const group = (c.role_group as string | undefined) || 'others';
    if (!byGroup[group]) byGroup[group] = [];
    byGroup[group].push({ ...c, index: i });
  });

  return GROUP_ORDER.map(({ key, label }) => {
    const rows = byGroup[key] || [];
    // Sub-group by company (case-insensitive trim, null → "(no company)").
    const bySub: Record<string, Array<Contact & { index: number }>> = {};
    for (const r of rows) {
      const company = (r.company || '').trim();
      const subKey = company.toLowerCase() || '__none__';
      if (!bySub[subKey]) bySub[subKey] = [];
      bySub[subKey].push(r);
    }
    const subGroups: SubGroup[] = Object.entries(bySub).map(([k, rs]) => ({
      companyKey: k,
      companyDisplay: k === '__none__' ? null : (rs[0].company || null),
      maxTouch: Math.max(0, ...rs.map((r) => r.touch_count || 0)),
      contacts: rs
        .slice()
        .sort((a, b) => {
          const ta = a.touch_count || 0;
          const tb = b.touch_count || 0;
          if (ta !== tb) return tb - ta;
          return (a.name || '').localeCompare(b.name || '');
        }),
    }));
    subGroups.sort((a, b) => {
      // (no company) goes last; otherwise max(touch) desc, then name.
      if (a.companyKey === '__none__') return 1;
      if (b.companyKey === '__none__') return -1;
      if (a.maxTouch !== b.maxTouch) return b.maxTouch - a.maxTouch;
      return (a.companyDisplay || '').localeCompare(b.companyDisplay || '');
    });
    return { key, label, subGroups };
  });
}
